import React, { useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import classNames from 'classnames';
import { timeAgo } from 'js/utils/time-ago';
import { useSelector } from 'react-redux';

import { Card, CardBody, Button, FormGroup, Input } from 'reactstrap';
import { Icon, IconStack } from 'js/components';
import { DocumentProvider } from 'js/ydoc/doc/component';
import {
  EditorProvider,
  useEditorContext,
} from 'js/ydoc/docs/capabilities-assessment/EditorContext';

import SummaryTool from 'js/components/Assessment/Editors/CapabilitiesAssessmentEditor/Tools/SummaryTool';
import {
  faCopy,
  faRocketLaunch,
  faPaperPlane,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faChevronLeft,
  faEnvelope,
  faCircleNotch,
} from '@fortawesome/pro-regular-svg-icons';
import NotificationOption from './NotificationOption';
import { faBan } from '@fortawesome/pro-light-svg-icons';
import { Link, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import { useNotify } from 'js/hooks/useNotify';
import { allowSamlForParticipants } from 'js/utils/entitlement-utils';
import { useWorkspaceContext } from 'js/contexts/WorkspaceContext';
import { CohortList } from './CohortList';

import editorStyles from 'js/components/Assessment/Editors/common/EditorCommon.module.scss';
import styles from './CustomizeAssessment.module.scss';
import AsyncButton from 'js/components/Forms/AsyncButton';
import ErrorMessage from 'js/components/Assessment/Editors/common/components/EditorErrors/ErrorMessage';

function formatDate(date) {
  return date ? format(date, 'yyyy-MM-dd HH:mm') : '';
}

function DateDistance({ date }) {
  if (!date) {
    return null;
  }

  return timeAgo(date);
}

/** Why "Go live!" is disabled, said next to it rather than on hover. */
function GoLiveBlocker({ errorCount, hasGoLiveDate }) {
  if (errorCount > 0) {
    return (
      <div
        className={classNames(
          'invalid-feedback force-show',
          styles['go-live-blocker']
        )}
      >
        <ErrorMessage>
          {errorCount === 1
            ? 'There is 1 issue to fix before this can go live.'
            : `There are ${errorCount} issues to fix before this can go live.`}{' '}
          They're listed in the summary.
        </ErrorMessage>
      </div>
    );
  }

  if (!hasGoLiveDate) {
    return (
      <div className={classNames('text-muted', styles['go-live-blocker'])}>
        Set a go live date to go live.
      </div>
    );
  }

  return null;
}

function LaunchConfiguration({
  userGroupAssessment,
  userGroup,
  onFieldChanges,
  refreshAssessment,
  onResendNotifications,
}) {
  const token = useSelector((state) => state.identity.token);

  if (!token) {
    return null;
  }

  return (
    <DocumentProvider
      channelProviderToken={token}
      channelProviderTopic={`crdt:user-group-assessment:${userGroupAssessment.crdt_doc_id}`}
    >
      <EditorProvider userGroupAssessment={userGroupAssessment}>
        <LaunchConfigurationInternals
          userGroupAssessment={userGroupAssessment}
          userGroup={userGroup}
          onFieldChanges={onFieldChanges}
          refreshAssessment={refreshAssessment}
          onResendNotifications={onResendNotifications}
        />
      </EditorProvider>
    </DocumentProvider>
  );
}

function LaunchConfigurationInternals({
  userGroupAssessment,
  userGroup,
  onFieldChanges,
  refreshAssessment,
  onResendNotifications = () => {},
}) {
  const { workspaceId, userGroupId, assessmentId } = useParams();
  const notify = useNotify();
  const { errors } = useEditorContext();
  const { workspace } = useWorkspaceContext();

  const handleCopyLink = useCallback(() => {
    copy(
      `${window.location.origin}/assessments/${userGroupAssessment.assessment_id}/run`
    );

    notify({ type: 'success', message: 'Link copied to clipboard' });
  }, [userGroupAssessment.assessment_id, notify]);

  const errorCount = errors ? errors.length : 0;
  useEffect(() => {
    if (
      errorCount &&
      userGroupAssessment.status === 'published' &&
      userGroupAssessment.published_at &&
      userGroupAssessment.published_at < new Date()
    ) {
      onFieldChanges({
        status: 'draft',
      });
    }
  }, [
    errorCount,
    userGroupAssessment.published_at,
    userGroupAssessment.status,
    onFieldChanges,
  ]);

  const handlePublish = async () => {
    await onFieldChanges({
      status: 'published',
    });

    // onAssessmentCreate(assessment);
  };

  const handleUnpublish = () => {
    onFieldChanges({
      status: 'draft',
    });
  };

  const handleNotificationOptionSelectedChange = useCallback(
    (type, selected) => {
      const currentConfig = userGroupAssessment.notification_config || {};
      const optionConfig = currentConfig[type] || {
        enabled: false,
        useCustomMessage: false,
        message: '',
      };

      onFieldChanges({
        notification_config: {
          ...currentConfig,
          [type]: {
            ...optionConfig,
            enabled: selected,
          },
        },
      });
    },
    [userGroupAssessment.notification_config, onFieldChanges]
  );

  const handleNotificationOptionMessageChange = useCallback(
    (type, message) => {
      const currentConfig = userGroupAssessment.notification_config || {};
      const optionConfig = currentConfig[type] || {
        enabled: false,
        useCustomMessage: false,
        message: '',
      };

      onFieldChanges({
        notification_config: {
          ...currentConfig,
          [type]: {
            ...optionConfig,
            message,
            useCustomMessage: true,
          },
        },
      });
    },
    [userGroupAssessment.notification_config, onFieldChanges]
  );

  const handleNotificationOptionCustomMessageToggle = useCallback(
    (type, useCustomMessage) => {
      const currentConfig = userGroupAssessment.notification_config || {};
      const optionConfig = currentConfig[type] || {
        enabled: false,
        useCustomMessage: false,
        message: '',
      };

      onFieldChanges({
        notification_config: {
          ...currentConfig,
          [type]: {
            ...optionConfig,
            enabled: useCustomMessage || optionConfig.enabled,
            useCustomMessage,
          },
        },
      });
    },
    [userGroupAssessment.notification_config, onFieldChanges]
  );

  const handleResendNotifications = useCallback(() => {
    return onResendNotifications();
  }, [onResendNotifications]);

  const hasNotification =
    userGroupAssessment.notification_config?.email?.enabled;

  useEffect(() => {
    const now = new Date();
    const goLiveAt = new Date(userGroupAssessment.go_live_at);
    const timerRefs = { interval: null, timeout: null };

    const clearTimers = () => {
      if (timerRefs.interval) {
        clearInterval(timerRefs.interval);
      }

      if (timerRefs.timeout) {
        clearTimeout(timerRefs.timeout);
      }
    };

    if (userGroupAssessment.status === 'published') {
      if (now < goLiveAt) {
        const timeUntilGoLive = goLiveAt.getTime() - now.getTime();

        clearTimers();
        timerRefs.timeout = setTimeout(() => {
          refreshAssessment();
        }, timeUntilGoLive);
      } else {
        timerRefs.interval = setInterval(() => {
          refreshAssessment();
        }, 10000);
      }
    }

    return () => clearTimers();
  }, [
    userGroupAssessment.status,
    userGroupAssessment.go_live_at,
    refreshAssessment,
  ]);

  if (errors == null) {
    return null;
  }

  return (
    <div className={editorStyles['editor-grid']}>
      <div>
        <Card>
          <CardBody>
            <section>
              <h5>First, some basics</h5>
              <div className={styles['launch-dates']}>
                <FormGroup>
                  <label>Go live at</label>
                  <div>
                    {!userGroupAssessment.went_live_at ? (
                      <Input
                        type="datetime-local"
                        style={{ maxWidth: '250px' }}
                        value={formatDate(userGroupAssessment.go_live_at)}
                        onChange={(e) =>
                          onFieldChanges({
                            go_live_at: new Date(e.target.value),
                          })
                        }
                        step={900}
                      />
                    ) : (
                      format(
                        userGroupAssessment.go_live_at,
                        'MMM d, yyyy h:mm a'
                      )
                    )}
                  </div>
                  <div className="text-muted">
                    <DateDistance date={userGroupAssessment.go_live_at} />
                  </div>
                </FormGroup>
                <FormGroup>
                  <label>Close results at</label>
                  <div>
                    <Input
                      type="datetime-local"
                      style={{ maxWidth: '250px' }}
                      value={formatDate(userGroupAssessment.close_at)}
                      onChange={(e) =>
                        onFieldChanges({
                          close_at: new Date(e.target.value),
                        })
                      }
                      step={900}
                    />
                  </div>
                  <div className="text-muted">
                    <DateDistance date={userGroupAssessment.close_at} />
                  </div>
                </FormGroup>
              </div>
            </section>

            {/* <section>
              <h5>Choose your audience</h5>
              <p>Who is allowed to participate in this assessment?</p>
            </section> */}
            {workspace?.account && allowSamlForParticipants(workspace.account) && (
              <section>
                <FormGroup>
                  <label htmlFor="authentication-type">Authentication</label>
                  <Input
                    type="select"
                    id="authentication-type"
                    value={userGroupAssessment.authentication_type || 'NONE'}
                    onChange={(e) =>
                      onFieldChanges({
                        authentication_type: e.target.value,
                      })
                    }
                    style={{ width: 'unset', height: 'unset' }}
                  >
                    <option value="NONE">No authentication required</option>
                    <option value="SAML_ONLY">Single sign-on</option>
                  </Input>
                </FormGroup>
              </section>
            )}
            <section className="mt-5">
              <div className={styles['notify-members-header']}>
                <div>
                  <h5>Let your members know when it's live</h5>
                </div>
                {userGroupAssessment.went_live_at && hasNotification && (
                  <div className={styles['resend-notifications-button']}>
                    <AsyncButton
                      color="primary"
                      onClick={handleResendNotifications}
                    >
                      <Icon
                        icon={faPaperPlane}
                        className="icon-left async-hide-loading"
                        fixedWidth
                      />
                      <Icon
                        icon={faCircleNotch}
                        className="icon-left async-show-loading"
                        fixedWidth
                        spin
                      />
                      Resend notifications
                    </AsyncButton>
                  </div>
                )}
              </div>

              <div className={styles['notify-options']}>
                <NotificationOption
                  type="email"
                  icon={faEnvelope}
                  label="Email"
                  config={userGroupAssessment.notification_config?.email}
                  onSelectedChange={handleNotificationOptionSelectedChange}
                  onMessageChange={handleNotificationOptionMessageChange}
                  onCustomMessageToggle={
                    handleNotificationOptionCustomMessageToggle
                  }
                />
              </div>

              {userGroup && hasNotification && (
                <>
                  <p>Notify these members when the assessment is live</p>
                  <CohortList
                    members={userGroup.members}
                    config={userGroupAssessment.access_config}
                  />
                </>
              )}
            </section>

            <section>
              {!userGroupAssessment.went_live_at && (
                <Link
                  to={`/w/${workspaceId}/pm/user-groups/${userGroupId}/assessments/${assessmentId}/customize`}
                  className="btn btn-secondary"
                  disabled={userGroupAssessment.status === 'published'}
                >
                  <Icon icon={faChevronLeft} className="icon-left" /> Edit
                  questions
                </Link>
              )}
              {userGroupAssessment.status === 'draft' && (
                <Button
                  color="primary"
                  onClick={handlePublish}
                  disabled={
                    errors.length > 0 || !userGroupAssessment.go_live_at
                  }
                >
                  <Icon icon={faRocketLaunch} className="icon-left" /> Go live!
                </Button>
              )}
              {userGroupAssessment.status === 'published' && (
                <Button color="danger" onClick={handleUnpublish}>
                  <IconStack className="icon-left">
                    <Icon
                      icon={faRocketLaunch}
                      className="icon-left"
                      transform="shrink-5"
                    />
                    <Icon
                      icon={faBan}
                      className="icon-left"
                      transform="grow-5"
                    />
                  </IconStack>
                  Cancel launch
                </Button>
              )}
              {userGroupAssessment.assessment_id && (
                <Button color="success" onClick={handleCopyLink}>
                  <Icon icon={faCopy} className="icon-left" /> Copy link
                </Button>
              )}
              {userGroupAssessment.status === 'draft' && (
                <GoLiveBlocker
                  errorCount={errorCount}
                  hasGoLiveDate={!!userGroupAssessment.go_live_at}
                />
              )}
            </section>
          </CardBody>
        </Card>
      </div>
      <div>
        <SummaryTool />
      </div>
    </div>
  );
}

export default LaunchConfiguration;
